import type { GencowCtx } from "./context.js";

/**
 * @deprecated Legacy `httpAction` handler type.
 * Use `httpRoute` and expose routes via `defineApi({ httpRoutes })` instead.
 */
export type HttpActionHandler = (ctx: GencowCtx, req: HttpActionRequest) => Promise<HttpActionResponse>;

/**
 * @deprecated Legacy `httpAction` request type.
 * Use `httpRoute` and expose routes via `defineApi({ httpRoutes })` instead.
 */
export interface HttpActionRequest {
  /** HTTP method (GET, POST, PUT, DELETE, PATCH) */
  method: string;
  /** Full URL */
  url: string;
  /** URL path (e.g. /api/cli/auth-start) */
  path: string;
  /** Route params (e.g. { id: "abc" } for /api/apps/:id) */
  params: Record<string, string>;
  /** Query string params */
  query: Record<string, string>;
  /** Request headers */
  headers: Record<string, string>;
  /** Parse JSON body */
  json: <T = unknown>() => Promise<T>;
  /** Parse form data */
  formData: () => Promise<FormData>;
  /** Raw body as ArrayBuffer */
  arrayBuffer: () => Promise<ArrayBuffer>;
  /** Raw body as text */
  text: () => Promise<string>;
}

/**
 * @deprecated Legacy `httpAction` response type.
 * Use `httpRoute` and expose routes via `defineApi({ httpRoutes })` instead.
 */
export interface HttpActionResponse {
  status?: number;
  headers?: Record<string, string>;
  body?: unknown;
}

/**
 * @deprecated Legacy `httpAction` definition.
 * Use `httpRoute` and expose routes via `defineApi({ httpRoutes })` instead.
 */
export interface HttpActionDef {
  /** HTTP method */
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  /** URL path (Hono 패턴 — /api/cli/:code 형태 지원) */
  path: string;
  /** true = 인증 없이 접근 가능, false(기본) = auth 필수 */
  isPublic: boolean;
  /** 핸들러 */
  handler: HttpActionHandler;
}

// ─── httpAction registry (globalThis) ───────────────────
// globalThis shared singleton — dual-bundle safe (see analysis-dual-module-registry.md).

declare global {
  var __gencow_httpActionRegistry: HttpActionDef[];
}

if (!globalThis.__gencow_httpActionRegistry) globalThis.__gencow_httpActionRegistry = [];

/**
 * @deprecated Internal legacy registry.
 * Use `httpRoute` and expose routes via `defineApi({ httpRoutes })` instead.
 */
export const httpActionRegistry = globalThis.__gencow_httpActionRegistry;

/**
 * 커스텀 HTTP 엔드포인트를 선언적으로 등록합니다.
 * query/mutation은 RPC 패턴이지만, httpAction은 RESTful HTTP 라우트를 직접 정의합니다.
 *
 * @deprecated Use `httpRoute` and expose routes via `defineApi({ httpRoutes })` instead.
 *
 * 사용 사례:
 *   - CLI Device Auth (POST /api/cli/auth-start)
 *   - 파일 업로드 (POST /api/apps/:id/deploy)
 *   - Webhook 수신
 *   - 외부 서비스 콜백
 *
 * @example
 * ```typescript
 * import { httpAction } from "@gencow/core";
 *
 * export const authStart = httpAction({
 *     method: "POST",
 *     path: "/api/cli/auth-start",
 *     public: true,
 *     handler: async (ctx, req) => {
 *         const code = crypto.randomUUID().slice(0, 8);
 *         return { body: { code, url: `https://gencow.com/cli-auth?code=${code}` } };
 *     },
 * });
 * ```
 */
export function httpAction(def: {
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  public?: boolean;
  handler: HttpActionHandler;
}): HttpActionDef {
  const actionDef: HttpActionDef = {
    method: def.method,
    path: def.path,
    isPublic: def.public === true,
    handler: def.handler,
  };
  httpActionRegistry.push(actionDef);
  return actionDef;
}

/**
 * @deprecated Legacy HTTP action registry API.
 * Use `httpRoute` and expose routes via `defineApi({ httpRoutes })` instead.
 */
export function getRegisteredHttpActions(): HttpActionDef[] {
  return [...httpActionRegistry];
}
